import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from '@material-ui/core';
import { t } from 'ttag';
import clsx from 'clsx';
import useStyles from './styles';
import Close from '@icons/Close';
import { Button, IconButton } from '@coolremote/shared';
import useBacNet from '@hooks/useBacNet';

/**
 * ActivePrioritiesDialog component for displaying and managing active BACnet priorities.
 *
 * @param {Object} props
 * @param {string} props.bacNetObjectId - The ID of the BACnet object.
 * @param {function} props.handleClose - Callback to handle closing the dialog.
 * @param {string} props.bacnetUnitId - The ID of the BACnet unit.
 */

const ActivePrioritiesDialog = ({
  bacNetObjectId,
  handleClose,
  bacnetUnitId,
}) => {
  const classes = useStyles();
  const { getBacNetPriorityArray, resetBacNetPriority } = useBacNet();
  const [bacnetPriorities, setBacnetPriorities] = useState({});
  const [loading, setLoading] = useState(true);

  const { hasActivePriorities, priorityEntries } = useMemo(() => {
    const entries = Object.entries(bacnetPriorities || {});
    return {
      priorityEntries: entries,
      hasActivePriorities: entries.some(([_, isActive]) => isActive),
    };
  }, [bacnetPriorities]);

  const fetchPriorities = async () => {
    if (bacnetUnitId && bacNetObjectId) {
      setLoading(true);
      try {
        const result = await getBacNetPriorityArray(
          bacnetUnitId,
          bacNetObjectId
        );
        setBacnetPriorities(result ?? {});
      } catch (error) {
        setBacnetPriorities({});
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchPriorities();
  }, [bacNetObjectId, bacnetUnitId]);

  const handlePriorityRelease = async (priorityKey) => {
    setLoading(true);
    try {
      await resetBacNetPriority(bacnetUnitId, bacNetObjectId, priorityKey);
      // Refetch priorities after successful release
      await fetchPriorities();
    } catch (error) {
      console.error('Error releasing BACnet priority:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      disableEnforceFocus
      fullScreen
      classes={{ paper: classes.dialogPaper }}
      aria-labelledby="simple-dialog-title"
      open={true}
    >
      <div className={classes.dialogHeader}>
        <Typography className={classes.headerTitle}>
          {t`Active Priorities`}
        </Typography>
        <div>
          <Button
            onClick={() => {
              handlePriorityRelease('all');
            }}
            disabled={!hasActivePriorities || loading}
            width={100}
          >{t`Release All`}</Button>
          <IconButton
            disableRipple
            className={classes.iconBtnStyle}
            onClick={handleClose}
          >
            <Close color="#7f7692" />
          </IconButton>
        </div>
      </div>
      <TableContainer className={classes.tableContainer}>
        <Table stickyHeader aria-label="a dense table">
          <TableHead>
            <TableRow>
              <TableCell
                classes={{ root: classes.tableHeadCell }}
                align="center"
              >
                {t`Priority`}
              </TableCell>
              <TableCell
                classes={{ root: classes.tableHeadCell }}
                align="center"
              >
                {t`Active`}
              </TableCell>
              <TableCell
                classes={{ root: classes.tableHeadCell }}
                align="center"
              >
                {t`Action`}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={3}
                  align="center"
                  classes={{
                    root: clsx(classes.tableCell, classes.loaderCell),
                  }}
                >
                  <CircularProgress size={40} />
                </TableCell>
              </TableRow>
            ) : (
              priorityEntries.map(([priorityKey, isActive]) => (
                <TableRow key={`row-${priorityKey}`}>
                  <TableCell
                    classes={{
                      root: classes.tableCell,
                    }}
                    align="center"
                  >
                    {priorityKey}
                  </TableCell>
                  <TableCell
                    classes={{
                      root: classes.tableCell,
                    }}
                    align="center"
                  >
                    {isActive ? t`True` : t`False`}
                  </TableCell>
                  <TableCell
                    classes={{
                      root: classes.tableCell,
                    }}
                    align="center"
                  >
                    <Button
                      onClick={() => {
                        handlePriorityRelease(priorityKey);
                      }}
                      width={100}
                      disabled={!isActive || loading}
                    >{t`Release`}</Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Dialog>
  );
};

export default ActivePrioritiesDialog;
